import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesAPI } from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

const MAZES = [
  {
    grid: [
      [1,1,1,1,1,1,1,1,1],
      [1,2,0,0,1,0,0,0,1],
      [1,1,1,0,1,0,1,0,1],
      [1,0,0,0,0,0,1,0,1],
      [1,0,1,1,1,0,1,0,1],
      [1,0,0,0,1,0,0,0,1],
      [1,1,1,0,1,1,1,0,1],
      [1,0,0,0,0,0,0,3,1],
      [1,1,1,1,1,1,1,1,1],
    ],
    start: { r: 1, c: 1 },
    end: { r: 7, c: 7 }
  },
  {
    grid: [
      [1,1,1,1,1,1,1,1,1],
      [1,2,1,0,0,0,1,0,1],
      [1,0,1,0,1,0,1,0,1],
      [1,0,0,0,1,0,0,0,1],
      [1,0,1,1,1,1,1,0,1],
      [1,0,0,0,0,0,1,0,1],
      [1,1,1,0,1,0,1,0,1],
      [1,0,0,0,1,0,0,3,1],
      [1,1,1,1,1,1,1,1,1],
    ],
    start: { r: 1, c: 1 },
    end: { r: 7, c: 7 }
  }
];

const CELL = 36;

export default function MazeScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const [mazeIndex] = useState(() => Math.floor(Math.random() * MAZES.length));
  const maze = MAZES[mazeIndex];

  const [pos, setPos] = useState(maze.start);
  const [moves, setMoves] = useState(0);
  const [won, setWon] = useState(false);
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(null);

  useEffect(() => {
    if (won) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(t);
  }, [won, startTime]);

  const move = useCallback((dr, dc) => {
    if (won) return;
    const nr = pos.r + dr;
    const nc = pos.c + dc;
    const cell = maze.grid[nr]?.[nc];
    if (cell === undefined || cell === 1) return;
    setPos({ r: nr, c: nc });
    setMoves(m => m + 1);
    if (cell === 3) {
      setWon(true);
      saveResult(moves + 1, Math.floor((Date.now() - startTime) / 1000));
    }
  }, [pos, won, maze, moves, startTime]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowUp')    move(-1, 0);
      if (e.key === 'ArrowDown')  move(1, 0);
      if (e.key === 'ArrowLeft')  move(0, -1);
      if (e.key === 'ArrowRight') move(0, 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move]);

  const saveResult = async (totalMoves, duration) => {
    if (!profile) return;
    try {
      const score = Math.max(0, 1000 - totalMoves * 10 - duration * 3);
      const result = await gamesAPI.saveResult({ profileId: profile.id, gameId: 'maze-escape', score, durationSeconds: duration, metadata: { moves: totalMoves } });
      setPointsEarned(result.pointsEarned);
      refreshProfile();
    } catch (e) { console.error(e); }
  };

  const reset = () => {
    setPos(maze.start);
    setMoves(0);
    setWon(false);
    setElapsed(0);
    setPointsEarned(null);
  };

  const getCellStyle = (cell, r, c) => {
    const isPlayer = r === pos.r && c === pos.c;
    const base = { width: CELL, height: CELL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0, borderRadius: 4, transition: 'background 0.1s' };
    if (isPlayer) return { ...base, background: '#667eea', fontSize: '1.4rem' };
    if (cell === 1) return { ...base, background: '#2C3E50' };
    if (cell === 3) return { ...base, background: '#E8F5E9', border: '2px solid var(--green)' };
    if (cell === 2) return { ...base, background: '#FFF9E6' };
    return { ...base, background: '#F8F9FA' };
  };

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #45B7D1, #2980B9)', paddingBottom: 20 }}>
        <button className="btn-back" onClick={() => navigate('/games')} style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem' }}>🌀 Maze Escape!</h1>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>Moves: {moves} · ⏱ {elapsed}s</div>
        </div>
        <button onClick={reset} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10, padding: '6px 12px', color: 'white', fontWeight: 700, cursor: 'pointer' }}>
          🔄 Reset
        </button>
      </div>

      <div className="screen-body" style={{ paddingTop: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: '0.85rem', color: 'var(--gray)' }}>
          <span>🟦 = You</span>
          <span>🟩 = Exit</span>
          <span>⬛ = Wall</span>
        </div>

        <div style={{ border: '3px solid #2C3E50', borderRadius: 8, overflow: 'hidden', marginBottom: 24, boxShadow: 'var(--shadow)' }}>
          {maze.grid.map((row, r) => (
            <div key={r} style={{ display: 'flex' }}>
              {row.map((cell, c) => (
                <div key={c} style={getCellStyle(cell, r, c)}>
                  {r === pos.r && c === pos.c ? '🚶' : cell === 3 ? '🚩' : cell === 2 ? '🌟' : ''}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* D-Pad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gridTemplateRows: 'repeat(3, 64px)', gap: 8 }}>
          {[
            [null, { dr: -1, dc: 0, label: '⬆️' }, null],
            [{ dr: 0, dc: -1, label: '⬅️' }, null, { dr: 0, dc: 1, label: '➡️' }],
            [null, { dr: 1, dc: 0, label: '⬇️' }, null]
          ].map((row, ri) =>
            row.map((btn, ci) => (
              <div key={`${ri}-${ci}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {btn ? (
                  <button onPointerDown={() => move(btn.dr, btn.dc)} style={{ width: 60, height: 60, borderRadius: 16, border: 'none', background: '#667eea', fontSize: '1.8rem', cursor: 'pointer', boxShadow: '0 5px 0 #4a5eb5', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none', WebkitUserSelect: 'none' }}>
                    {btn.label}
                  </button>
                ) : <div />}
              </div>
            ))
          )}
        </div>

        <p style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: 12 }}>Or use arrow keys on keyboard 🎮</p>
      </div>

      {won && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div className="card" style={{ textAlign: 'center', maxWidth: 320, width: '100%' }}>
            <div style={{ fontSize: '4rem', marginBottom: 8 }}>🎉</div>
            <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--green)', fontSize: '1.6rem' }}>You Escaped!</h2>
            <p style={{ color: 'var(--gray)', margin: '8px 0 16px' }}>{moves} moves in {elapsed} seconds!</p>
            {pointsEarned && (
              <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 12, padding: 10, marginBottom: 16 }}>
                <strong>⭐ +{pointsEarned} points!</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={reset}>Play Again</button>
              <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => navigate('/games')}>Games</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
